import React, { useState } from 'react';
import { FaTimes, FaArrowRight } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { Auth } from 'aws-amplify';
import appConfig from '../../../../config/app-config';

const { address: API_URL, key: API_KEY } = appConfig.apis.contractsManagementApiEnv;

const AMENDMENT_OPTIONS = [
    { value: 'CLONE', label: 'Clonar este contrato', amendment_type: 'clone', operation_type_id: 0 },
    { value: 'RECEIVABLES_ASSIGNMENT', label: 'Novo como cessão de recebível', amendment_type: 'new', operation_type_id: 2 },
    { value: 'STRUCTURED_OPERATION', label: 'Novo como operação estruturada', amendment_type: 'new', operation_type_id: 3 },
] as const;

const AMENDMENT_REASONS = [
    { value: 'renegotiation', label: 'Renegociação' },
    { value: 'restructuring', label: 'Reestruturação' },
    { value: 'not_applicable', label: 'N/A' },
] as const;

type AmendmentValue = typeof AMENDMENT_OPTIONS[number]['value'];
type AmendmentReason = typeof AMENDMENT_REASONS[number]['value'];

interface AmendmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    creditOperationId: string | number;
    onSuccess?: (data: any) => void;
}

const AmendmentModal: React.FC<AmendmentModalProps> = ({ isOpen, onClose, creditOperationId, onSuccess }) => {
    const [selected, setSelected] = useState<AmendmentValue | ''>('');
    const [reason, setReason] = useState<AmendmentReason | ''>('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleConfirm = async () => {
        if (!selected || !reason) {
            toast.error('Selecione o tipo e o motivo do aditamento');
            return;
        }

        const option = AMENDMENT_OPTIONS.find(o => o.value === selected);
        if (!option) return;

        setIsSubmitting(true);
        try {
            const token = (await Auth.currentSession()).getIdToken().getJwtToken();

            const body = {
                amendment_type: option.amendment_type,
                operation_type_id: option.operation_type_id,
                amendment_reason: reason,
            };

            const response = await fetch(`${API_URL}/operations/${creditOperationId}/amend`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': API_KEY || '',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            });

            if (response.ok) {
                const data = await response.json();
                toast.success('Aditamento criado com sucesso!');
                setSelected('');
                setReason('');
                onClose();
                onSuccess?.(data);
            } else {
                const error = await response.json().catch(() => ({}));
                toast.error(error.message || 'Erro ao criar aditamento');
            }
        } catch (error: unknown) {
            toast.error('Erro de conexão ao criar aditamento');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setSelected('');
        setReason('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-lg w-full max-w-md">
                <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold dark:text-white">Aditar Contrato</h3>
                    <button type="button" onClick={handleClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                        <FaTimes size={18} />
                    </button>
                </div>

                <div className="p-4 space-y-5">
                    <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tipo de aditamento:</p>
                        <div className="space-y-2">
                            {AMENDMENT_OPTIONS.map(option => (
                                <label
                                    key={option.value}
                                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${selected === option.value
                                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-400'
                                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                                        }`}
                                >
                                    <input
                                        type="radio"
                                        name="amendment_type"
                                        value={option.value}
                                        checked={selected === option.value}
                                        onChange={() => setSelected(option.value)}
                                        className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                                    />
                                    <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-200">{option.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Motivo:</p>
                        <div className="space-y-2">
                            {AMENDMENT_REASONS.map(opt => (
                                <label
                                    key={opt.value}
                                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${reason === opt.value
                                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-400'
                                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                                        }`}
                                >
                                    <input
                                        type="radio"
                                        name="amendment_reason"
                                        value={opt.value}
                                        checked={reason === opt.value}
                                        onChange={() => setReason(opt.value)}
                                        className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                                    />
                                    <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-200">{opt.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
                    <button type="button" onClick={handleClose} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 rounded text-sm">
                        Cancelar
                    </button>
                    <button type="button"
                        onClick={handleConfirm}
                        disabled={!selected || !reason || isSubmitting}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white rounded text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <FaArrowRight /> {isSubmitting ? 'Criando...' : 'Confirmar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AmendmentModal;
